import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  useAgreementList,
  defaultFilters,
  type AgreementFilters,
  type AgreementSummary,
} from '../hooks/useAgreements';

const KATEGORI_OPTIONS = [
  { value: '', label: 'Alle kategorier' },
  { value: 'Kontrakt', label: 'Kontrakt' },
  { value: 'Forskrift', label: 'Forskrift' },
  { value: 'Vedtak', label: 'Vedtak' },
  { value: 'Interkommunalt samarbeid', label: 'Interkommunalt' },
  { value: 'Annet', label: 'Annet' },
];

const STATUS_OPTIONS = [
  { value: 'true', label: 'Aktive' },
  { value: 'false', label: 'Inaktive' },
];

const STALE_OPTIONS = [
  { value: '', label: 'Alle' },
  { value: 'true', label: 'Kun foreldede' },
  { value: 'false', label: 'Kun oppdaterte' },
];

function formatCurrency(value: number | null): string {
  if (value == null) return '\u2014';
  return value.toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

function formatRisk(value: number | null): string {
  if (value == null) return '\u2014';
  return `${(value * 100).toFixed(0)}%`;
}

function riskChipColor(value: number | null): 'error' | 'warning' | 'success' | 'default' {
  if (value == null) return 'default';
  if (value >= 0.7) return 'error';
  if (value >= 0.4) return 'warning';
  return 'success';
}

const columns: GridColDef[] = [
  {
    field: 'Tittel',
    headerName: 'Tittel',
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <Link component={RouterLink} to={`/agreements/${params.row.AvtaleID}`} underline="hover">
        {params.value}
      </Link>
    ),
  },
  { field: 'Betaler', headerName: 'Betaler', width: 160 },
  {
    field: 'Kategori',
    headerName: 'Kategori',
    width: 140,
    renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
  },
  {
    field: 'Beloep_gjeldende',
    headerName: 'Beløp',
    width: 130,
    type: 'number',
    valueFormatter: (value: number | null) => formatCurrency(value),
  },
  {
    field: 'Risiko_sannsynlighet',
    headerName: 'Risiko',
    width: 100,
    renderCell: (params) => (
      <Chip
        label={formatRisk(params.value)}
        size="small"
        color={riskChipColor(params.value)}
      />
    ),
  },
  {
    field: 'Sist_endret',
    headerName: 'Sist endret',
    width: 120,
    valueFormatter: (value: string | null) =>
      value ? new Date(value).toLocaleDateString('nb-NO') : '\u2014',
  },
];

export function AgreementListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<AgreementFilters>(() => {
    const initial = { ...defaultFilters };
    const inntektslinjeID = searchParams.get('inntektslinjeID');
    const ansvar = searchParams.get('ansvar');
    if (inntektslinjeID) initial.inntektslinjeID = inntektslinjeID;
    if (ansvar) initial.ansvar = ansvar;
    return initial;
  });
  const { data, isLoading, error } = useAgreementList(filters);

  function updateFilter(patch: Partial<AgreementFilters>) {
    setFilters((prev) => ({ ...prev, page: 1, ...patch }));
  }

  const agreements = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, pageSize: 50, totalCount: 0 };

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h2">Avtaler</Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          href={`/api/admin/export/agreements?erAktiv=${filters.erAktiv}${filters.kategori ? `&kategori=${filters.kategori}` : ''}`}
          download
          size="small"
        >
          Eksporter CSV
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Søk i tittel, betaler, beskrivelse..."
          value={filters.search}
          onChange={(e) => updateFilter({ search: e.target.value })}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="kategori-label">Kategori</InputLabel>
          <Select
            labelId="kategori-label"
            value={filters.kategori}
            label="Kategori"
            onChange={(e) => updateFilter({ kategori: e.target.value })}
          >
            {KATEGORI_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={filters.erAktiv}
            label="Status"
            onChange={(e) => updateFilter({ erAktiv: e.target.value })}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="revisjon-label">Revisjon</InputLabel>
          <Select
            labelId="revisjon-label"
            value={filters.stale}
            label="Revisjon"
            onChange={(e) => updateFilter({ stale: e.target.value })}
          >
            {STALE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {(filters.inntektslinjeID || filters.ansvar) && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setSearchParams({});
                updateFilter({ inntektslinjeID: '', ansvar: '' });
              }}
            >
              Fjern filter
            </Button>
          }
        >
          {filters.inntektslinjeID
            ? `Viser avtaler koblet til inntektslinje ${filters.inntektslinjeID}`
            : `Viser avtaler for ansvar ${filters.ansvar}`}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Kunne ikke laste avtaler: {error.message}
        </Alert>
      )}

      <DataGrid
        rows={agreements}
        columns={columns}
        getRowId={(row) => row.AvtaleID}
        loading={isLoading}
        paginationMode="server"
        rowCount={pagination.totalCount}
        paginationModel={{ page: filters.page - 1, pageSize: pagination.pageSize }}
        onPaginationModelChange={(model) => {
          setFilters((prev) => ({ ...prev, page: model.page + 1 }));
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        autoHeight
        sx={{ bgcolor: 'background.paper' }}
      />
    </Box>
  );
}
